import { isApiName } from 'ts-components/PiChat/protocol';
import type { KiCodePrompt } from './session/promptDelivery';
import type { KiCodeResume } from './session/resumeStorage';
import {
  isSameKiCodeTarget,
  isValidKiCodeTarget,
  type KiCodeOpenArgs,
  type KiCodeTarget,
} from './profiles';

export interface KiCodeSessionState {
  key: number;
  target: KiCodeTarget;
  newConversation: boolean;
  prompt: KiCodePrompt | null;
  resumeId: string | null;
}

export interface KiCodeState {
  available: boolean;
  identity: string | null;
  counter: number;
  session: KiCodeSessionState | null;
}

export type KiCodeAction =
  | { type: 'open'; args: KiCodeOpenArgs }
  | { type: 'close' }
  | { type: 'new_conversation' }
  | { type: 'app_created'; appApiName: string }
  | {
      type: 'availability';
      available: boolean;
      identity: string | null;
      resume: KiCodeResume | null;
    };

export const INITIAL_KICODE_STATE: KiCodeState = {
  available: false,
  identity: null,
  counter: 0,
  session: null,
};

export const kiCodeReducer = (
  state: KiCodeState,
  action: KiCodeAction
): KiCodeState => {
  switch (action.type) {
    case 'open': {
      const { initialPrompt, ...target } = action.args;
      if (!state.available || !isValidKiCodeTarget(target)) return state;
      const counter = state.counter + 1;
      const prompt = initialPrompt
        ? { id: counter, text: initialPrompt }
        : null;
      if (state.session && isSameKiCodeTarget(state.session.target, target)) {
        return prompt
          ? { ...state, counter, session: { ...state.session, prompt } }
          : state;
      }
      return {
        ...state,
        counter,
        session: {
          key: counter,
          target,
          newConversation: false,
          prompt,
          resumeId: null,
        },
      };
    }
    case 'close':
      return state.session ? { ...state, session: null } : state;
    case 'new_conversation': {
      if (!state.session) return state;
      const counter = state.counter + 1;
      return {
        ...state,
        counter,
        session: {
          key: counter,
          target: state.session.target,
          newConversation: true,
          prompt: null,
          resumeId: null,
        },
      };
    }
    case 'app_created': {
      const { session } = state;
      if (
        !session ||
        session.target.context.appApiName !== undefined ||
        !isApiName(action.appApiName)
      ) {
        return state;
      }
      return {
        ...state,
        session: {
          ...session,
          target: {
            profile: 'business-app',
            context: {
              targetType: 'business_app',
              appApiName: action.appApiName,
            },
          },
        },
      };
    }
    case 'availability': {
      const { available, identity, resume } = action;
      const session =
        available && identity === state.identity ? state.session : null;
      if (
        !available ||
        session ||
        !resume ||
        !isValidKiCodeTarget(resume.target)
      ) {
        return { ...state, available, identity, session };
      }
      const counter = state.counter + 1;
      return {
        ...state,
        available,
        identity,
        counter,
        session: {
          key: counter,
          target: resume.target,
          newConversation: false,
          prompt: null,
          resumeId: resume.conversationId,
        },
      };
    }
    default:
      return state;
  }
};
